import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Send,
  Eye,
  Mail,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { api } from '../services/api';
import type { QuarterlyStats, ReportRun, Project } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function getPreviousQuarter(): { year: number; quarter: number } {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  if (currentQ === 1) return { year: now.getFullYear() - 1, quarter: 4 };
  return { year: now.getFullYear(), quarter: currentQ - 1 };
}

function getQuarterLabel(quarter: number, year: number): string {
  const months: Record<number, string> = {
    1: 'Jan – Mär',
    2: 'Apr – Jun',
    3: 'Jul – Sep',
    4: 'Okt – Dez',
  };
  return `Q${quarter} ${year} (${months[quarter]})`;
}

function formatBytes(bytes: string): string {
  const n = parseInt(bytes, 10);
  if (isNaN(n) || n === 0) return '0 Bytes';
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} Bytes`;
}

// ─── Vorschau-Modal ───────────────────────────────────────────────────────────

function PreviewModal({
  projectId,
  projectName,
  year,
  quarter,
  onClose,
}: {
  projectId: string;
  projectName: string;
  year: number;
  quarter: number;
  onClose: () => void;
}) {
  const { data: stats, isLoading, error } = useQuery<QuarterlyStats>({
    queryKey: ['report-preview', projectId, year, quarter],
    queryFn: () => api.reports.preview(projectId, year, quarter),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{projectName}</h2>
            <p className="text-sm text-gray-500">{getQuarterLabel(quarter, year)} – Vorschau</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-600 py-4">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">Fehler beim Laden der Daten</span>
            </div>
          )}

          {stats && (
            <div className="space-y-4">
              {/* Backups */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Backups</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.backupsCompleted}</p>
                  <p className="text-sm text-green-700 font-medium">
                    {stats.backupsFailed > 0
                      ? `${stats.backupsFailed} fehlgeschlagen`
                      : 'alle erfolgreich'}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Volumen</p>
                  <p className="text-2xl font-bold text-gray-900">{formatBytes(stats.totalStorageBytes)}</p>
                  <p className="text-sm text-blue-700 font-medium">gesichertes Datenvolumen</p>
                </div>
              </div>

              {/* Updates */}
              {(stats.wpUpdatesApprox > 0 || stats.themeUpdatesApprox > 0 || stats.pluginPushes > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  {(stats.wpUpdatesApprox > 0 || stats.themeUpdatesApprox > 0) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        WP-Updates <span className="text-gray-400 normal-case">(ca.)</span>
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {stats.wpUpdatesApprox + stats.themeUpdatesApprox}
                      </p>
                      <p className="text-sm text-yellow-700">
                        {stats.wpUpdatesApprox} Plugins, {stats.themeUpdatesApprox} Themes
                      </p>
                    </div>
                  )}
                  {stats.pluginPushes > 0 && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Plugin-Rollouts</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.pluginPushes}</p>
                      <p className="text-sm text-sky-700">via BackupPilot</p>
                    </div>
                  )}
                </div>
              )}

              {/* Validator */}
              {(stats.validatorPassed + stats.validatorFailed > 0) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Backup-Validierungen</p>
                  <p className="text-sm text-gray-700">
                    <span className="text-green-700 font-semibold">{stats.validatorPassed} bestanden</span>
                    {stats.validatorFailed > 0 && (
                      <> · <span className="text-red-700 font-semibold">{stats.validatorFailed} fehlgeschlagen</span></>
                    )}
                  </p>
                </div>
              )}

              {/* Hinweis Schätzwert */}
              {(stats.wpUpdatesApprox > 0 || stats.themeUpdatesApprox > 0) && (
                <p className="text-xs text-gray-400 leading-relaxed">
                  * WP-Plugin/Theme-Updates sind Schätzwerte (ca.) basierend auf Health-Check-Verläufen.
                  Plugin-Rollouts via BackupPilot sind exakte Werte.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 pb-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────

export default function Reports() {
  const prev = getPreviousQuarter();
  const [selectedYear, setSelectedYear] = useState(prev.year);
  const [selectedQuarter, setSelectedQuarter] = useState(prev.quarter);
  const [previewProject, setPreviewProject] = useState<{ id: string; name: string } | null>(null);
  const [sendingProjectId, setSendingProjectId] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<Record<string, { success: boolean; message: string }>>({});

  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - i);

  // Projekte laden
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 1, 'all', '', 'name', 'asc'],
    queryFn: () => api.projects.list({ page: 1, limit: 200, sortBy: 'name', sortOrder: 'asc' }),
  });

  // Alle ReportRuns laden
  const { data: reportRuns } = useQuery<ReportRun[]>({
    queryKey: ['reports'],
    queryFn: () => api.reports.list(),
  });

  // Lookup: projectId → letzter ReportRun für dieses Q/Jahr
  const reportRunMap = useMemo(() => {
    const map: Record<string, ReportRun> = {};
    if (!reportRuns) return map;
    for (const run of reportRuns) {
      if (run.year === selectedYear && run.quarter === selectedQuarter) {
        // reportRuns sind nach sentAt desc sortiert → erster Treffer = aktuellster
        if (!map[run.projectId]) map[run.projectId] = run;
      }
    }
    return map;
  }, [reportRuns, selectedYear, selectedQuarter]);

  const sendMutation = useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      api.reports.send(projectId, selectedYear, selectedQuarter),
    onMutate: ({ projectId }) => setSendingProjectId(projectId),
    onSuccess: (data, { projectId }) => {
      setSendingProjectId(null);
      setSendResult(prev => ({ ...prev, [projectId]: { success: data.success, message: data.message } }));
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      // Meldung nach 5s ausblenden
      setTimeout(() => setSendResult(prev => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      }), 5000);
    },
    onError: (err: Error, { projectId }) => {
      setSendingProjectId(null);
      setSendResult(prev => ({ ...prev, [projectId]: { success: false, message: err.message } }));
      setTimeout(() => setSendResult(prev => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      }), 5000);
    },
  });

  const projects: Project[] = projectsData?.projects ?? [];
  const projectsWithEmail = projects.filter(p => p.customerEmail);
  const projectsWithoutEmail = projects.filter(p => !p.customerEmail);

  const handleSendAll = () => {
    for (const project of projectsWithEmail) {
      const existing = reportRunMap[project.id];
      if (!existing || !existing.success) {
        sendMutation.mutate({ projectId: project.id });
      }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Berichte</h1>
          <p className="text-gray-500 mt-1">Quartalsberichte für Kunden generieren und versenden</p>
        </div>
      </div>

      {/* Quartal-Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (selectedQuarter === 1) { setSelectedYear(y => y - 1); setSelectedQuarter(4); }
              else setSelectedQuarter(q => q - 1);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {[1, 2, 3, 4].map(q => (
            <button
              key={q}
              onClick={() => setSelectedQuarter(q)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedQuarter === q
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Q{q}
            </button>
          ))}

          <button
            onClick={() => {
              if (selectedQuarter === 4) { setSelectedYear(y => y + 1); setSelectedQuarter(1); }
              else setSelectedQuarter(q => q + 1);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <select
          value={selectedYear}
          onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <span className="text-sm text-gray-500 ml-2">{getQuarterLabel(selectedQuarter, selectedYear)}</span>

        <div className="ml-auto">
          <button
            onClick={handleSendAll}
            disabled={projectsWithEmail.length === 0 || sendMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Alle senden ({projectsWithEmail.length})
          </button>
        </div>
      </div>

      {/* Projekttabelle */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {projectsLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Noch keine Projekte angelegt</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Projekt</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Kunden-E-Mail</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Letzter Bericht</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {projects.map(project => {
                const run = reportRunMap[project.id];
                const isSending = sendingProjectId === project.id;
                const result = sendResult[project.id];

                return (
                  <tr key={project.id} className="hover:bg-gray-50">
                    {/* Projekt */}
                    <td className="px-6 py-4">
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        {project.name}
                      </Link>
                      <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{project.url}</p>
                    </td>

                    {/* Kunden-E-Mail */}
                    <td className="px-6 py-4">
                      {project.customerEmail ? (
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-700">{project.customerEmail}</span>
                        </div>
                      ) : (
                        <Link
                          to={`/projects/${project.id}`}
                          className="text-xs text-gray-400 hover:text-primary-600 underline underline-offset-2"
                        >
                          Nicht konfiguriert
                        </Link>
                      )}
                    </td>

                    {/* Letzter Bericht */}
                    <td className="px-6 py-4">
                      {run ? (
                        <div>
                          <p className="text-sm text-gray-700">
                            {formatDistanceToNow(new Date(run.sentAt), { addSuffix: true, locale: de })}
                          </p>
                          <p className="text-xs text-gray-400">an {run.emailTo}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">–</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      {result ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                          result.success
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {result.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {result.success ? 'Gesendet' : 'Fehler'}
                        </span>
                      ) : run ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                          run.success
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {run.success ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                          {run.success ? 'Gesendet' : 'Fehlgeschlagen'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                          <Clock className="w-3.5 h-3.5" />
                          Ausstehend
                        </span>
                      )}
                    </td>

                    {/* Aktionen */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setPreviewProject({ id: project.id, name: project.name })}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Vorschau
                        </button>
                        <button
                          onClick={() => sendMutation.mutate({ projectId: project.id })}
                          disabled={!project.customerEmail || isSending}
                          title={!project.customerEmail ? 'Keine Kunden-E-Mail konfiguriert' : undefined}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSending ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5" />
                          )}
                          {isSending ? 'Wird gesendet…' : 'Senden'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Projekte ohne E-Mail Hinweis */}
        {projectsWithoutEmail.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-3 bg-gray-50">
            <p className="text-xs text-gray-500">
              {projectsWithoutEmail.length} {projectsWithoutEmail.length === 1 ? 'Projekt hat' : 'Projekte haben'} keine Kunden-E-Mail konfiguriert.{' '}
              Kunden-E-Mail kann in den Projektdetails gesetzt werden.
            </p>
          </div>
        )}
      </div>

      {/* Vorschau-Modal */}
      {previewProject && (
        <PreviewModal
          projectId={previewProject.id}
          projectName={previewProject.name}
          year={selectedYear}
          quarter={selectedQuarter}
          onClose={() => setPreviewProject(null)}
        />
      )}
    </div>
  );
}
